import { useState, useEffect } from "react";
import { Button } from "../../../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";

export type AttributeFormData = {
    name: string;
    friendlyName: string;
    dataType: string;
    unit: string;
    min: string;
    max: string;
    positiveSuffix: string;
    negativeSuffix: string;
    trueValue: string;
    falseValue: string;
    trueDataValue: string;
    falseDataValue: string;
};

const defaultFormData: AttributeFormData = {
    name: "",
    friendlyName: "",
    dataType: "number",
    unit: "",
    min: "",
    max: "",
    positiveSuffix: "",
    negativeSuffix: "",
    trueValue: "",
    falseValue: "",
    trueDataValue: "",
    falseDataValue: "",
};

type AttributeDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (data: AttributeFormData) => void;
    initialData?: any;
    isPending: boolean;
};

export const AttributeDialog = ({ open, onOpenChange, onSave, initialData, isPending }: AttributeDialogProps) => {
    const [form, setForm] = useState<AttributeFormData>(defaultFormData);
    const isEditing = !!initialData;

    useEffect(() => {
        if (open) {
            if (initialData) {
                setForm({
                    name: initialData.name || "",
                    friendlyName: initialData.friendlyName || "",
                    dataType: initialData.dataType || "number",
                    unit: initialData.unit || "",
                    min: initialData.min != null ? String(initialData.min) : "",
                    max: initialData.max != null ? String(initialData.max) : "",
                    positiveSuffix: initialData.positiveSuffix || "",
                    negativeSuffix: initialData.negativeSuffix || "",
                    trueValue: initialData.trueValue || "",
                    falseValue: initialData.falseValue || "",
                    trueDataValue: initialData.trueDataValue || "",
                    falseDataValue: initialData.falseDataValue || "",
                });
            } else {
                setForm(defaultFormData);
            }
        }
    }, [open, initialData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(form);
    };

    const updateField = (field: keyof AttributeFormData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring";
    const labelClass = "text-xs font-medium text-muted-foreground uppercase tracking-wide";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEditing ? "Edit Attribute" : "Add Attribute"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Name *</label>
                            <input
                                value={form.name}
                                onChange={(e) => updateField("name", e.target.value)}
                                required
                                placeholder="e.g. engine_temp"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Friendly Name</label>
                            <input
                                value={form.friendlyName}
                                onChange={(e) => updateField("friendlyName", e.target.value)}
                                placeholder="e.g. Engine Temperature"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Data Type *</label>
                            <Select value={form.dataType} onValueChange={(v) => updateField("dataType", v)}>
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="boolean">Boolean</SelectItem>
                                    <SelectItem value="string">String</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Unit</label>
                            <input
                                value={form.unit}
                                onChange={(e) => updateField("unit", e.target.value)}
                                placeholder="e.g. °C, V, RPM"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    {form.dataType === "number" && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Min Value</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={form.min}
                                        onChange={(e) => updateField("min", e.target.value)}
                                        placeholder="Minimum"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Max Value</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={form.max}
                                        onChange={(e) => updateField("max", e.target.value)}
                                        placeholder="Maximum"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Positive Suffix</label>
                                    <input
                                        value={form.positiveSuffix}
                                        onChange={(e) => updateField("positiveSuffix", e.target.value)}
                                        placeholder="e.g. forward"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Negative Suffix</label>
                                    <input
                                        value={form.negativeSuffix}
                                        onChange={(e) => updateField("negativeSuffix", e.target.value)}
                                        placeholder="e.g. reverse"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {form.dataType === "boolean" && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>True Display Value</label>
                                    <input
                                        value={form.trueValue}
                                        onChange={(e) => updateField("trueValue", e.target.value)}
                                        placeholder="e.g. On"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>False Display Value</label>
                                    <input
                                        value={form.falseValue}
                                        onChange={(e) => updateField("falseValue", e.target.value)}
                                        placeholder="e.g. Off"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>True Data Value</label>
                                    <input
                                        value={form.trueDataValue}
                                        onChange={(e) => updateField("trueDataValue", e.target.value)}
                                        placeholder="e.g. 1"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>False Data Value</label>
                                    <input
                                        value={form.falseDataValue}
                                        onChange={(e) => updateField("falseDataValue", e.target.value)}
                                        placeholder="e.g. 0"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <DialogFooter className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? "Saving..." : isEditing ? "Save Changes" : "Add Attribute"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
